import { useCallback, useRef } from 'react';
import { Extension } from '@tiptap/react';
import { Plugin, TextSelection } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { Node, Fragment, Slice } from '@tiptap/pm/model';
import { nanoid } from 'nanoid';
import { scrapeHtmlWeb } from 'scrape-html-web';
import { getShareData } from '../../../services/projectServices';
import { getFigmaPreviewUrl } from '../../../services/taskServices';
import {
  DEFAULT_UPLOAD_FOLDER,
  getFirebaseUrlsFromFiles,
  isEmpty,
  isValidUrl,
  renderStatusIconName
} from '../../../helpers/common';
import { dispatchAppError } from '../../../services/appService';
import { CommentFilesInterface } from '../../../interfaces/TaskInterface';
import { captureException } from '../../../services/logService';
import { registerPastedFile } from '../imageUploadEmbed';
import { rootStore } from '../../../mobx/rootStore';
import {
  PRIORITY_ICONS,
  PROJECT_STATUS_COLOR,
  STATUS_ICON,
  STATUS_TYPE,
  TASK_PRIORITY,
  TASK_PRIORITY_LABELS
} from '../../../global/constants';
import { ScrapeHtmlWebOptions } from '../../../interfaces/tiptapInterface';
import { SettingIntegrationInterface } from '../../../interfaces/SettingsInterface';

interface UseEmbedPluginParams {
  settingIntegration: SettingIntegrationInterface | null | undefined;
  isAllowForFigmaPreview: boolean;
  setIsAllowForFigmaPreview: (value: boolean) => void;
  setLoading?: (value: boolean) => void;
  setStateLoading?: (value: boolean) => void;
  addUploadedFiles?: (files: CommentFilesInterface[]) => void;
  themeMode: { theme?: string } | undefined;
  editorInstanceId?: string;
  handleFileRead: (file: File) => Promise<unknown>;
  /** Storage sub-folder for files/images uploaded from this editor. Defaults to `files/`. */
  uploadFolder?: string;
}

export function handleUrlPaste(view: EditorView, clipboardData: string) {
  try {
    const url = new URL(clipboardData);
    const { schema, tr, selection } = view.state;
    const { from, to } = selection;
    const linkMark = schema.marks.link.create({ href: url.href });
    const linkTextNode = schema.text(clipboardData, [linkMark]);
    const spaceNode = schema.text(' ');
    const transaction = tr.replaceWith(from, to, [linkTextNode, spaceNode]);
    const newPos = from + linkTextNode.nodeSize + spaceNode.nodeSize;
    transaction.setSelection(TextSelection.create(transaction.doc, newPos));
    view.dispatch(transaction);
    return true;
  } catch {
    const textNode = view.state.schema.text(clipboardData);
    const fallbackTransaction = view.state.tr.replaceSelectionWith(textNode);
    view.dispatch(fallbackTransaction);
    return false;
  }
}

function insertNodeWithParagraph(view: EditorView, node: Node) {
  let transaction = view.state.tr.replaceSelectionWith(node);
  const endPos = transaction.doc.content.size;
  const emptyParagraph = view.state.schema.nodes.paragraph.create();
  transaction = transaction.insert(endPos, emptyParagraph);
  view.dispatch(transaction);
}

function insertLinkText(view: EditorView, clipboardData: string) {
  const { schema, tr, selection } = view.state;
  const { from, to } = selection;
  const linkMark = schema.marks.link.create({ href: clipboardData });
  const linkTextNode = schema.text(clipboardData, [linkMark]);
  const spaceNode = schema.text(' ');
  const transaction = tr.replaceWith(from, to, [linkTextNode, spaceNode]);
  const newPos = from + linkTextNode.nodeSize + spaceNode.nodeSize;
  transaction.setSelection(TextSelection.create(transaction.doc, newPos));
  view.dispatch(transaction);
}

function insertPlainText(view: EditorView, text: string) {
  const textNode = view.state.schema.text(text);
  const fallbackTransaction = view.state.tr.replaceSelectionWith(textNode);
  view.dispatch(fallbackTransaction);
}

// An external image src is one we should try to re-host on our own storage so every viewer
// can load it. Skips images already on Firebase storage and non-http(s) srcs (data:/blob:).
function isRehostableExternalImage(src: unknown): src is string {
  if (typeof src !== 'string' || !/^https?:\/\//i.test(src)) return false;
  if (src.startsWith('https://firebasestorage.googleapis.com')) return false;
  if (src.startsWith('https://storage.googleapis.com')) return false;
  return true;
}

// Gmail inline-image URLs (mail.google.com/mail/...) are tied to the pasting user's auth session
// and never load for anyone else. When we cannot re-host such an image, it must be dropped rather
// than left in the doc as a permanently broken image.
function isPrivateAuthenticatedImage(src: string): boolean {
  try {
    return new URL(src).hostname === 'mail.google.com';
  } catch {
    return false;
  }
}

function findImageNodePos(view: EditorView, nodeId: string): number {
  let found = -1;
  view.state.doc.descendants((node, pos) => {
    if (found !== -1) return false;
    if (node.type.name === 'imageUploadEmbed' && node.attrs.nodeId === nodeId) {
      found = pos;
      return false;
    }
    return true;
  });
  return found;
}

function replaceImageNodeSrc(view: EditorView, nodeId: string, url: string) {
  const pos = findImageNodePos(view, nodeId);
  if (pos === -1) return;
  const node = view.state.doc.nodeAt(pos);
  if (!node) return;
  const tr = view.state.tr.setNodeMarkup(pos, undefined, {
    ...node.attrs,
    src: url,
    isUploading: false,
    progress: 100
  });
  view.dispatch(tr);
}

function removeImageNode(view: EditorView, nodeId: string) {
  const pos = findImageNodePos(view, nodeId);
  if (pos === -1) return;
  const node = view.state.doc.nodeAt(pos);
  if (!node) return;
  view.dispatch(view.state.tr.delete(pos, pos + node.nodeSize));
}

// Re-host externally-referenced images pasted into the editor onto our storage so all users can
// load them. Publicly fetchable images are uploaded and their src swapped to the storage URL.
// Gmail (auth-only) images that cannot be fetched are dropped with a notice; other fetch failures
// leave the original URL in place (it may still be publicly reachable for other viewers).
async function rehostPastedExternalImages(
  view: EditorView,
  targets: { nodeId: string; src: string }[],
  folder: string
) {
  let droppedPrivateCount = 0;

  await Promise.all(
    targets.map(async ({ nodeId, src }) => {
      try {
        const response = await fetch(src);
        if (!response.ok) throw new Error(`Failed to fetch pasted image: ${response.status}`);
        const blob = await response.blob();
        if (!blob.type.startsWith('image/')) throw new Error('Pasted resource is not an image');

        const extension = blob.type.split('/')[1] || 'png';
        const file = new File([blob], `pasted_${nanoid(6)}.${extension}`, { type: blob.type });
        const [uploadedUrl] = await getFirebaseUrlsFromFiles([{ file }], folder);
        if (!uploadedUrl) throw new Error('Pasted image upload returned no URL');

        replaceImageNodeSrc(view, nodeId, uploadedUrl);
      } catch (error) {
        captureException(error);
        if (isPrivateAuthenticatedImage(src)) {
          droppedPrivateCount += 1;
          removeImageNode(view, nodeId);
        }
        // Non-private images: leave the original URL untouched — it may still load for other users.
      }
    })
  );

  if (droppedPrivateCount > 0) {
    dispatchAppError(
      droppedPrivateCount === 1
        ? "An image couldn't be imported and was removed. Please attach it manually."
        : `${droppedPrivateCount} images couldn't be imported and were removed. Please attach them manually.`
    );
  }
}

// Walk a pasted slice, tag each rehostable external image with a fresh nodeId (so it can be
// located again once its async upload resolves), and collect the work to run after insertion.
function collectAndTagExternalImages(
  fragment: Fragment,
  targets: { nodeId: string; src: string }[]
): Fragment {
  const children: Node[] = [];
  fragment.forEach((child) => {
    if (child.type.name === 'imageUploadEmbed' && isRehostableExternalImage(child.attrs.src)) {
      const nodeId = nanoid();
      targets.push({ nodeId, src: child.attrs.src });
      children.push(child.type.create({ ...child.attrs, nodeId }, child.content, child.marks));
    } else if (child.content.size > 0) {
      children.push(child.copy(collectAndTagExternalImages(child.content, targets)));
    } else {
      children.push(child);
    }
  });
  return Fragment.fromArray(children);
}

export function useEmbedPlugin({
  settingIntegration,
  isAllowForFigmaPreview,
  setIsAllowForFigmaPreview,
  setLoading,
  setStateLoading,
  addUploadedFiles,
  themeMode,
  editorInstanceId,
  handleFileRead,
  uploadFolder = DEFAULT_UPLOAD_FOLDER
}: UseEmbedPluginParams) {
  // The ProseMirror plugin below is built once when the editor mounts, so its drop/paste
  // handlers would otherwise close over the upload folder from that first render. Read it
  // through a ref that we refresh every render so uploads always use the current folder
  // even if projectId/taskId (and therefore uploadFolder) resolve after mount.
  // Same for addUploadedFiles, so drops append to the latest files.
  const uploadFolderRef = useRef(uploadFolder);
  uploadFolderRef.current = uploadFolder;
  const addUploadedFilesRef = useRef(addUploadedFiles);
  addUploadedFilesRef.current = addUploadedFiles;
  const createEmbedPlugin = useCallback(() => {
    return Extension.create({
      name: 'embedPlugin',

      addOptions() {
        return {
          getSettings: () => ({ settingIntegration, isAllowForFigmaPreview }),
          setLoading: null,
          dispatch: null,
          scrapeHtmlWeb: null
        };
      },

      addProseMirrorPlugins() {
        return [
          new Plugin({
            props: {
              transformPasted(slice, view) {
                const targets: { nodeId: string; src: string }[] = [];
                const content = collectAndTagExternalImages(slice.content, targets);
                if (targets.length === 0) return slice;
                // Insertion happens synchronously right after this returns; the async re-host runs
                // afterwards and locates each node by its tagged nodeId.
                rehostPastedExternalImages(view, targets, uploadFolderRef.current);
                return new Slice(content, slice.openStart, slice.openEnd);
              },
              handleDrop(view, event, slice, moved) {
                event.preventDefault();
                const { schema } = view.state;
                const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
                if (coords && event.dataTransfer?.files) {
                  const files: File[] = Array.from(event.dataTransfer.files);
                  const imageFiles = files?.filter((file: File) => file?.type.startsWith('image/'));
                  const otherFiles = files?.filter((file: File) => !file.type.startsWith('image/'));

                  if (imageFiles && imageFiles?.length > 0) {
                    const currentSetLoading = setLoading;
                    const currentSetStateLoading = setStateLoading;

                    const nodesToInsert: Node[] = [];

                    for (let i = 0; i < imageFiles.length; i++) {
                      const file = imageFiles[i];
                      const blobUrl = URL.createObjectURL(file);
                      const uniqueId = nanoid();
                      registerPastedFile(uniqueId, file);

                      const attrs: Record<string, unknown> = {
                        nodeId: uniqueId,
                        src: blobUrl,
                        isUploading: true,
                        progress: 0,
                        uploadFolder: uploadFolderRef.current,
                        setLoading: (loading: boolean) => {
                          if (currentSetLoading) currentSetLoading(loading);
                          if (currentSetStateLoading) currentSetStateLoading(loading);
                        }
                      };
                      if (editorInstanceId) {
                        attrs.ownerId = editorInstanceId;
                      }

                      const uploadingNode = view.state.schema.nodes.imageUploadEmbed?.create(attrs);

                      if (uploadingNode) {
                        nodesToInsert.push(uploadingNode);
                      }
                    }

                    if (nodesToInsert.length > 0) {
                      let transaction = view.state.tr.replaceSelectionWith(nodesToInsert[0]);

                      for (let i = 1; i < nodesToInsert.length; i++) {
                        const currentPos = transaction.selection.to;
                        transaction = transaction.insert(currentPos, nodesToInsert[i]);
                      }

                      const endPos = transaction.doc.content.size;
                      const emptyParagraph = view.state.schema.nodes.paragraph.create();
                      transaction = transaction.insert(endPos, emptyParagraph);

                      view.dispatch(transaction);
                    }

                    return true;
                  }

                  if (otherFiles && otherFiles?.length > 0) {
                    (async () => {
                      try {
                        setStateLoading?.(true);

                        const fileData = await Promise.all(
                          otherFiles?.map(async (file) => {
                            const fileContent = await handleFileRead(file);
                            if (isEmpty(fileContent)) return null;
                            return { file };
                          })
                        );
                        const validFiles = fileData?.filter((file): file is { file: File } => file !== null);
                        const uploadedUrls = await getFirebaseUrlsFromFiles(validFiles, uploadFolderRef.current);

                        if (uploadedUrls && uploadedUrls?.length > 0) {
                          const referenceFiles: CommentFilesInterface[] = [];
                          uploadedUrls?.forEach((url: string, index: number) => {
                            const file = otherFiles[index];
                            const parts = file?.name.split('.');
                            const extension = parts[parts.length - 1];
                            const sizeInMB = file?.size / (1024 * 1024);

                            let sizeValue;
                            if (sizeInMB >= 1) {
                              sizeValue = `${sizeInMB.toFixed(2)} MB`;
                            } else {
                              const sizeInKB = file?.size / 1024;
                              sizeValue = `${sizeInKB.toFixed(2)} KB`;
                            }

                            if (['mp4', 'mov', 'mkv', 'webm'].includes(extension)) {
                              const videoNode = schema.nodes.snapEmbed.create({
                                url,
                                id: '',
                                recordingName: file?.name,
                                themeMode: themeMode?.theme || 'light'
                              });
                              const transaction = view.state.tr.insert(coords.pos, videoNode);
                              view.dispatch(transaction);
                            } else {
                              const newObj = {
                                name: file?.name,
                                href: url,
                                size: sizeValue,
                                file_type: extension
                              };
                              referenceFiles.push(newObj);
                            }
                          });
                          if (referenceFiles && referenceFiles?.length > 0)
                            addUploadedFilesRef.current?.(referenceFiles);
                        }
                      } catch (e) {
                        captureException(e);
                      } finally {
                        setStateLoading?.(false);
                      }
                    })();
                  }
                }
                return false;
              },
              handlePaste(view, event) {
                event.preventDefault();
                const clipboardData = event.clipboardData?.getData('Text');
                try {
                  // Handle pasted images
                  const items = Array.from(event.clipboardData?.items || []);
                  if (items?.length) {
                    for (const item of items) {
                      if (item.type.startsWith('image/')) {
                        const file = item.getAsFile();
                        if (file) {
                          const blobUrl = URL.createObjectURL(file);
                          const uniqueId = nanoid();
                          registerPastedFile(uniqueId, file);

                          const attrs: Record<string, unknown> = {
                            nodeId: uniqueId,
                            src: blobUrl,
                            isUploading: true,
                            progress: 0,
                            uploadFolder: uploadFolderRef.current,
                            setLoading: (loading: boolean) => {
                              if (setLoading) setLoading(loading);
                              if (setStateLoading) setStateLoading(loading);
                            }
                          };
                          if (editorInstanceId) {
                            attrs.ownerId = editorInstanceId;
                          }

                          const uploadingNode = view.state.schema.nodes.imageUploadEmbed?.create(attrs);

                          if (uploadingNode) {
                            let transaction = view.state.tr.replaceSelectionWith(uploadingNode);
                            const endPos = transaction.doc.content.size;
                            const emptyParagraph = view.state.schema.nodes.paragraph.create();
                            transaction = transaction.insert(endPos, emptyParagraph);
                            view.dispatch(transaction);
                          }

                          return true;
                        }
                      }
                    }
                  }

                  // Figma embed
                  if (clipboardData?.startsWith('https://www.figma.com')) {
                    (async () => {
                      try {
                        if (!settingIntegration?.figma_connected) {
                          insertLinkText(view, clipboardData);
                          return;
                        }
                        if (setLoading) setLoading(true);
                        if (!isValidUrl(clipboardData)) {
                          insertPlainText(view, clipboardData);
                          setIsAllowForFigmaPreview(false);
                          return false;
                        }
                        const url = new URL(clipboardData);
                        const fileId = url.pathname.split('/')[2];
                        const fileName = url.pathname.split('/')[3];
                        const nodes = url.searchParams.get('node-id');

                        const uniqueId = nanoid();

                        const loadingNode = view.state.schema.nodes.figmaEmbed.create({
                          frameNames: 'Loading...',
                          projectName: fileName,
                          filePreviewUrl: '',
                          url: clipboardData,
                          isLoading: true,
                          nodeId: uniqueId
                        });

                        let transaction = view.state.tr.replaceSelectionWith(loadingNode);
                        const endPos = transaction.doc.content.size;
                        const emptyParagraph = view.state.schema.nodes.paragraph.create();
                        transaction = transaction.insert(endPos, emptyParagraph);
                        view.dispatch(transaction);

                        const figmaData = await getFigmaPreviewUrl({ fileId, nodes });

                        if (figmaData && typeof figmaData === 'object' && 'filePreviewUrl' in figmaData) {
                          const finalNode = view.state.schema.nodes.figmaEmbed.create({
                            frameNames: figmaData?.frameNames,
                            projectName: figmaData?.projectName || 'Example Project Name',
                            filePreviewUrl: figmaData.filePreviewUrl,
                            url: clipboardData,
                            isLoading: false,
                            nodeId: uniqueId
                          });

                          view.state.doc.descendants((node, pos) => {
                            if (node.attrs.nodeId === uniqueId) {
                              const finalTransaction = view.state.tr.replaceWith(
                                pos,
                                pos + node.nodeSize,
                                finalNode
                              );
                              view.dispatch(finalTransaction);
                              return false;
                            }
                            return true;
                          });
                        } else {
                          view.state.doc.descendants((node, pos) => {
                            if (node.attrs.nodeId === uniqueId) {
                              const { schema } = view.state;
                              const linkMark = schema.marks.link.create({ href: clipboardData });
                              const linkTextNode = schema.text(clipboardData, [linkMark]);

                              const fallbackTransaction = view.state.tr.replaceWith(
                                pos,
                                pos + node.nodeSize,
                                linkTextNode
                              );
                              view.dispatch(fallbackTransaction);
                              return false;
                            }
                            return true;
                          });
                        }
                      } catch (e) {
                        captureException(e);
                      } finally {
                        if (setLoading) setLoading(false);
                      }
                    })();
                    return true;
                  }

                  // Figma embed state (non-preview)
                  else if (clipboardData?.includes('figma.com')) {
                    setIsAllowForFigmaPreview(false);
                  }

                  // Share snap card
                  else if (
                    clipboardData?.startsWith('https://share.teamcamp.app') &&
                    !clipboardData?.startsWith('https://share.teamcamp.app/docs')
                  ) {
                    (async () => {
                      try {
                        if (setLoading) setLoading(true);
                        const url = new URL(clipboardData);
                        const fileId = url.pathname.split('/')[1];
                        const share_data = await getShareData(fileId);
                        if (share_data) {
                          const refUrl =
                            typeof share_data?.ref === 'string' ? share_data?.ref : share_data?.ref[0] || '';
                          const finalSnapNode = view.state.schema.nodes.snapEmbed.create({
                            url: refUrl,
                            id: fileId,
                            recordingName: share_data?.name,
                            themeMode: themeMode?.theme || 'light'
                          });

                          insertNodeWithParagraph(view, finalSnapNode);
                        } else {
                          insertPlainText(view, clipboardData);
                          return true;
                        }
                      } catch (e) {
                        captureException(e);
                      } finally {
                        if (setLoading) setLoading(false);
                      }
                    })();
                    return true;
                  }

                  // Lightshot preview
                  else if (clipboardData && clipboardData.startsWith('https://prnt.sc')) {
                    (async () => {
                      try {
                        const matchUrl = clipboardData.match(/https:\/\/prnt\.sc\/\S+/)?.[0] || '';
                        if (setLoading) setLoading(true);

                        const url = new URL(matchUrl);
                        const options: ScrapeHtmlWebOptions = {
                          url,
                          bypassCors: true,
                          mainSelector: '.image__pic',
                          childrenSelector: [{ key: 'link', selector: 'a', attr: 'src' }]
                        };
                        const data = await scrapeHtmlWeb(options);

                        if (data[0]?.link) {
                          const finalLightshotNode = view.state.schema.nodes.lightshotEmbed.create({
                            url: clipboardData,
                            previewLink: data[0]?.link
                          });

                          insertNodeWithParagraph(view, finalLightshotNode);
                        } else {
                          insertPlainText(view, clipboardData);
                          return true;
                        }
                      } catch (e) {
                        captureException(e);
                      } finally {
                        if (setLoading) setLoading(false);
                      }
                    })();
                    return true;
                  }

                  // YouTube embed
                  else if (
                    (clipboardData?.startsWith('https://www.youtube.com') ||
                      clipboardData?.startsWith('https://youtu.be')) &&
                    isValidUrl(clipboardData)
                  ) {
                    (async () => {
                      try {
                        if (setLoading) setLoading(true);

                        let videoId = '';

                        if (clipboardData?.startsWith('https://www.youtube.com')) {
                          const url = new URL(clipboardData);
                          videoId = url.searchParams?.get('v') || '';
                        } else if (clipboardData?.startsWith('https://youtu.be')) {
                          const url = new URL(clipboardData);
                          videoId = url.pathname?.slice(1);
                        }

                        if (videoId) {
                          const previewImageUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
                          const finalNode = view.state.schema.nodes.youTubeEmbed.create({
                            url: clipboardData,
                            videoId,
                            previewImageUrl,
                            themeMode: themeMode?.theme || 'light'
                          });

                          insertNodeWithParagraph(view, finalNode);
                        } else {
                          insertPlainText(view, clipboardData);
                          return true;
                        }
                      } catch (e) {
                        captureException(e);
                      } finally {
                        if (setLoading) setLoading(false);
                      }
                    })();
                    return true;
                  }

                  // Task link embed
                  else if (clipboardData?.includes('/tasks/details/') && isValidUrl(clipboardData)) {
                    (async () => {
                      try {
                        if (setLoading) setLoading(true);

                        let url;
                        try {
                          url = new URL(clipboardData);
                        } catch (e) {
                          handleUrlPaste(view, clipboardData);
                          return true;
                        }
                        const pathSegments = url.pathname.split('/');
                        const taskId = pathSegments[pathSegments.length - 1];

                        if (!taskId) {
                          handleUrlPaste(view, clipboardData);
                          return true;
                        }

                        const taskDetails = rootStore.taskStore.tasks?.find((task) => task.id === taskId);
                        if (!taskDetails) {
                          handleUrlPaste(view, clipboardData);
                          return true;
                        }

                        const defaultStatus = taskDetails?.project?.statusData?.find(
                          (status) => status?.Default
                        );
                        const status =
                          taskDetails?.project?.statusData?.find(
                            (status) => status['_id'] === taskDetails?.statusId
                          ) || defaultStatus;
                        const defaultPriority = taskDetails?.project?.priority?.default;
                        const priorityName =
                          TASK_PRIORITY_LABELS[
                            taskDetails?.priority || defaultPriority || TASK_PRIORITY.NO_PRIORITY
                          ];
                        const statusType = status?.Type || STATUS_TYPE.UNSTARTED;
                        const statusIconClass = renderStatusIconName(statusType);
                        const assigneeUrls =
                          taskDetails?.users?.map((user) => user?.profile_photo || '').join(',') || '';
                        const assigneeNames =
                          taskDetails?.users?.map((user) => user?.name || 'Unknown').join(',') || 'Unknown';

                        const finalNode = view.state.schema.nodes.taskLinkEmbed.create({
                          url: clipboardData,
                          taskId: taskDetails?.id,
                          taskName: taskDetails?.name || 'Unknown',
                          projectName: taskDetails?.project?.name || 'Unknown',
                          priorityName: priorityName,
                          statusName: status?.Name || 'Unknown',
                          assigneeName: assigneeNames,
                          statusIconClass: statusIconClass,
                          assigneeUrls: assigneeUrls,
                          themeMode: themeMode?.theme || 'light',
                          statusUrl: taskDetails?.statusDetails?.Type
                            ? STATUS_ICON[taskDetails?.statusDetails?.Type]
                            : STATUS_ICON[2],
                          priorityUrl:
                            typeof taskDetails.priority === 'number'
                              ? PRIORITY_ICONS[taskDetails.priority]
                              : PRIORITY_ICONS[0],
                          showPriority: taskDetails?.project?.priority?.enabled || false
                        });

                        insertNodeWithParagraph(view, finalNode);
                      } catch (e) {
                        captureException(e);
                      } finally {
                        if (setLoading) setLoading(false);
                      }
                    })();
                    return true;
                  }

                  // Document link
                  else if (clipboardData?.includes('/docs') && isValidUrl(clipboardData)) {
                    (async () => {
                      handleUrlPaste(view, clipboardData);
                    })();
                    return true;
                  }

                  // Project link embed
                  else if (clipboardData?.includes('/projects') && isValidUrl(clipboardData)) {
                    (async () => {
                      try {
                        if (setLoading) setLoading(true);

                        let url;
                        try {
                          url = new URL(clipboardData);
                        } catch (e) {
                          handleUrlPaste(view, clipboardData);
                          return true;
                        }

                        const pathParts = url.pathname.split('/').filter(Boolean);
                        const projectIndex = pathParts.indexOf('projects');
                        const projectId = pathParts[projectIndex + 2];

                        if (!projectId) {
                          handleUrlPaste(view, clipboardData);
                          return true;
                        }

                        const projectDetails = rootStore.projectStore.projects?.find(
                          (project) => project.id === projectId
                        );

                        if (!projectDetails) {
                          handleUrlPaste(view, clipboardData);
                          return true;
                        }

                        if (!view.state.schema.nodes.projectLinkEmbed) {
                          captureException(new Error('projectLinkEmbed node type not found in schema'));
                          handleUrlPaste(view, clipboardData);
                          return true;
                        }

                        const finalNode = view.state.schema.nodes.projectLinkEmbed.create({
                          url: clipboardData,
                          projectId: projectId,
                          projectName: projectDetails?.name || 'Unknown Project',
                          projectDescription: projectDetails?.description || 'No description',
                          themeMode: themeMode?.theme || 'light',
                          projectStatusColor: PROJECT_STATUS_COLOR[projectDetails?.progressData?.status],
                          projectStatusText: `${projectDetails?.progressData?.statusText} (${projectDetails?.progressData?.progress}%)`
                        });

                        insertNodeWithParagraph(view, finalNode);
                      } catch (e) {
                        captureException(e);
                      } finally {
                        if (setLoading) setLoading(false);
                      }
                    })();
                    return true;
                  }
                  return false;
                } catch (e) {
                  captureException(e);
                  return false;
                }
              }
            }
          })
        ];
      }
    });
  }, [
    settingIntegration,
    isAllowForFigmaPreview,
    setIsAllowForFigmaPreview,
    setLoading,
    setStateLoading,
    themeMode,
    editorInstanceId,
    handleFileRead
  ]);

  return { createEmbedPlugin };
}
